import { useAtom, useAtomValue } from 'jotai';
import { SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';

import { IFundDataModel } from '../../../data-models/fund.data-model';
import { fundsAtom, fundsByIdMapAtom, selectableFundsAtom } from '../../../services/state/AppConfigStateJ';
import { SelectItem } from '../../../types';
import {
  activeFundsStateJ,
  createFundGroupResolver,
  portfolioAllFundsSelectedStateJ,
  checkedFundsStateJ,
} from '../state/FundFilterStateJ';
import { useDebouncedValue } from './useDebouncedValue';
import { useView } from './useView';

export interface IFundSelect {
  clearSelection: () => void;
  onSelect: (item: SelectItem) => void;
  onSelectAll: () => void;
  onSelectDeselectAll: () => void;
  onSelectGroup: (group: string) => void;
  onDeselectGroup: (group: string) => void;
  onChangeSelection: (newValue: SelectItem[]) => void;
}

export function useFundSelector() {
  const allFunds = useAtomValue(selectableFundsAtom);
  const fundsMap = useAtomValue(fundsByIdMapAtom);
  const everyFund = useAtomValue(fundsAtom);
  const groupOf = useMemo(() => createFundGroupResolver(everyFund), [everyFund]);
  const { setFundsFilter } = useView();

  const activeFundsFromView = useAtomValue(activeFundsStateJ);
  const activeFunds = useMemo(() => {
    return (
      activeFundsFromView?.reduce((acc, f) => {
        const fund = fundsMap.get(f.id);
        if (!fund) {
          return acc;
        }
        acc.push(fund);
        return acc;
      }, [] as IFundDataModel[]) ?? null
    );
  }, [activeFundsFromView, fundsMap]);

  const [checkedFunds, setCheckedFunds] = useAtom(checkedFundsStateJ);
  const debouncedLocalFunds = useDebouncedValue(checkedFunds, 200);

  // Tracks whether the current selection change came from a user interaction
  // (vs. the initial hydration of `checkedFunds` from the saved view). Only
  // user-driven changes should be persisted back to the view — see the
  // write-back effect below. (FD-2977)
  const hasUserChangedSelectionRef = useRef(false);

  // Wrap setCheckedFunds for all user-facing handlers so they flag the change
  // as user-driven. The hydration effect intentionally uses the raw setter.
  const userSetCheckedFunds = useCallback(
    (update: SetStateAction<IFundDataModel[] | null>) => {
      hasUserChangedSelectionRef.current = true;
      setCheckedFunds(update);
    },
    [setCheckedFunds]
  );

  const hasAllSelected = useAtomValue(portfolioAllFundsSelectedStateJ);

  useEffect(() => {
    const local = checkedFunds?.map((f) => f.id).toString() ?? '';
    const global = activeFunds?.map((f) => f.id).toString() ?? '';

    if (Array.isArray(checkedFunds) === Array.isArray(activeFunds) && local === global) {
      return;
    }

    const hasAllFundsSelected = activeFunds?.length === allFunds?.length;
    setCheckedFunds(hasAllFundsSelected ? null : activeFunds);
  }, [activeFunds, setCheckedFunds]); // eslint-disable-line

  useEffect(() => {
    // Only persist fund-filter changes that originate from a user interaction.
    // On initial load `selectedViewPFJ` suspends until views resolve, so this
    // hook's first render already has `activeFunds` populated from the saved
    // view while `checkedFunds`/`debouncedLocalFunds` are still their mount
    // value (`null`). Without this guard the effect can't tell "user cleared
    // the filter" (null) from "local state hasn't hydrated yet" (null) and
    // would write `filteredFunds: null` back — wiping the saved selection on
    // private views, which (unlike default/shared views) persist to the
    // server on every change. (FD-2977)
    if (!hasUserChangedSelectionRef.current) {
      return;
    }

    const local = debouncedLocalFunds?.map((f) => f.id).toString() ?? '';
    const global = activeFunds?.map((f) => f.id).toString() ?? '';

    if (Array.isArray(debouncedLocalFunds) === Array.isArray(activeFunds) && local === global) {
      return;
    }

    setFundsFilter(debouncedLocalFunds);
  }, [debouncedLocalFunds]); // eslint-disable-line

  // handler for mui autocomplete
  const onChangeSelection = useCallback(
    (newValue: SelectItem[]) => {
      userSetCheckedFunds(newValue.map((v) => fundsMap.get(v.id as number)!));
    },
    [fundsMap, userSetCheckedFunds]
  );

  const onSelect = useCallback(
    ({ id }: SelectItem) => {
      if (!allFunds) {
        return;
      }

      const clickedFund: IFundDataModel | undefined = fundsMap.get(id as number);
      if (!clickedFund) {
        return;
      }

      if (!checkedFunds || checkedFunds.length === allFunds.length) {
        const allExceptOne = allFunds.filter((af) => af.id !== clickedFund.id);
        return userSetCheckedFunds(allExceptOne);
      }

      const fundToRemove: IFundDataModel | undefined = checkedFunds?.find((f) => f.id === id);
      if (fundToRemove) {
        const reduced = checkedFunds?.filter((f) => f.id !== id) ?? [];
        return userSetCheckedFunds(reduced);
      }

      const newFundsList = [...(checkedFunds ?? []), clickedFund];
      const isAllSelected = newFundsList?.length === allFunds?.length;
      userSetCheckedFunds(isAllSelected ? null : newFundsList);
    },
    [allFunds, fundsMap, checkedFunds, userSetCheckedFunds]
  );

  const onSelectAll = useCallback(() => {
    if (!allFunds) {
      return;
    }

    userSetCheckedFunds(null);
  }, [allFunds, userSetCheckedFunds]);

  const inGroup = useCallback(
    (fund: IFundDataModel, group: string) => groupOf(fund).toLowerCase() === group.toLowerCase(),
    [groupOf]
  );

  const onSelectGroup = useCallback(
    (group: string) => {
      userSetCheckedFunds((prev) => {
        const previousItems = (prev ?? allFunds).filter((fund) => !inGroup(fund, group)) ?? null;
        const newItems = allFunds.filter((fund) => inGroup(fund, group)) ?? null;

        return !previousItems && !newItems ? null : [...(previousItems ?? []), ...(newItems ?? [])];
      });
    },
    [allFunds, inGroup, userSetCheckedFunds]
  );

  const onDeselectGroup = useCallback(
    (group: string) => {
      userSetCheckedFunds((prev) => {
        return (prev ?? allFunds).filter((fund) => !inGroup(fund, group));
      });
    },
    [allFunds, inGroup, userSetCheckedFunds]
  );

  const onSelectDeselectAll = useCallback(() => {
    if (!allFunds) {
      return;
    }
    if (hasAllSelected) {
      userSetCheckedFunds([]);
    } else {
      onSelectAll();
    }
  }, [allFunds, hasAllSelected, onSelectAll, userSetCheckedFunds]);

  const clearSelection = useCallback(() => {
    return userSetCheckedFunds([]);
  }, [userSetCheckedFunds]);

  const value: IFundSelect = useMemo(
    () => ({
      clearSelection,
      onSelect,
      onSelectAll,
      onSelectDeselectAll,
      onSelectGroup,
      onDeselectGroup,
      onChangeSelection,
    }),
    [
      clearSelection,
      onSelect,
      onSelectAll,
      onSelectDeselectAll,
      onSelectGroup,
      onDeselectGroup,
      onChangeSelection,
    ]
  );

  return value;
}
